import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, X } from "lucide-react";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const savedCard = params.get("saved");
  const [status, setStatus] = useState("checking");
  const { clear } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (savedCard) {
      setStatus("paid");
      if (!cleared.current) {
        cleared.current = true;
        clear();
      }
      return;
    }
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let attempts = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) {
            cleared.current = true;
            clear();
          }
          return;
        }
      } catch {
        /* riprova */
      }
      attempts += 1;
      if (attempts < 15) timer = setTimeout(poll, 2000);
      else setStatus("pending");
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId, clear]);

  return (
    <main className="min-h-screen flex items-center justify-center px-6" data-testid="payment-success-page">
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="text-center max-w-lg"
      >
        <div className="mx-auto h-16 w-16 rounded-full border border-primary/50 flex items-center justify-center mb-10">
          <Check size={22} className="text-primary" />
        </div>
        <p className="overline-label mb-5">Ordine confermato</p>
        <h1 className="font-serif font-light text-4xl md:text-6xl mb-8" data-testid="payment-success-heading">
          {status === "paid" ? (
            <>Grazie, <span className="italic text-primary">a presto.</span></>
          ) : status === "checking" ? (
            "Conferma in corso…"
          ) : (
            "Pagamento in elaborazione"
          )}
        </h1>
        <p className="text-muted-foreground tracking-wide leading-relaxed mb-12" data-testid="payment-success-message">
          {status === "paid"
            ? "Il tuo ordine è confermato. Ogni pezzo verrà preparato a mano nel nostro atelier: riceverai presto i dettagli via email."
            : "Stiamo verificando il pagamento con Stripe. Se hai completato il pagamento, l'ordine verrà registrato a breve."}
        </p>
        <Link
          to="/shop"
          data-testid="success-back-to-shop"
          className="inline-flex items-center gap-3 h-14 px-9 rounded-full border border-white/25 text-xs uppercase tracking-[0.25em] hover:border-primary hover:text-primary transition-colors duration-300"
        >
          Torna alla collezione
        </Link>
      </motion.div>
    </main>
  );
}

export function PaymentCancel() {
  return (
    <main className="min-h-screen flex items-center justify-center px-6" data-testid="payment-cancel-page">
      <motion.div
        initial={{ opacity: 0, y: 28 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
        className="text-center max-w-lg"
      >
        <div className="mx-auto h-16 w-16 rounded-full border border-white/20 flex items-center justify-center mb-10">
          <X size={22} className="text-muted-foreground" />
        </div>
        <p className="overline-label mb-5">Pagamento annullato</p>
        <h1 className="font-serif font-light text-4xl md:text-6xl mb-8">
          Nessun addebito, <span className="italic text-primary">nessun problema.</span>
        </h1>
        <p className="text-muted-foreground tracking-wide leading-relaxed mb-12">
          Il tuo carrello è ancora qui che ti aspetta.
        </p>
        <Link
          to="/checkout"
          data-testid="cancel-back-to-checkout"
          className="inline-flex items-center gap-3 h-14 px-9 rounded-full bg-primary text-primary-foreground text-xs uppercase tracking-[0.25em] font-medium hover:bg-[#c8ad8f] transition-colors duration-300"
        >
          Riprova il checkout
        </Link>
      </motion.div>
    </main>
  );
}
